import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {Object} ImportLogItem
 * @property {number} id
 * @property {string} tenant_id
 * @property {string} institution_id
 * @property {number} initiated_by
 * @property {"students"|"teachers"} type
 * @property {number} total_rows
 * @property {number} processed_rows
 * @property {number} success_count
 * @property {number} failed_count
 * @property {"pending"|"processing"|"completed"|"failed"|"partial"} status
 * @property {Object|null} meta
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @typedef {Object} GetImportLogsParams
 * @property {number} [page] - Page number for pagination
 * @property {number} [per_page] - Number of items per page
 * @property {Object} [filters] - Filters (e.g. { status: "completed", type: "students" })
 */

/**
 * Custom hook to fetch import logs history with pagination and filtering.
 * Supports filtering by status (pending, processing, completed, failed, partial)
 * and type (students, teachers).
 */
export const useImportLogsHistory = () => {
  const [logs, setLogs] = useState(
    /** @type {ImportLogItem[] | null} */ (null),
  );
  const [meta, setMeta] = useState(/** @type {PaginationMeta | null} */ (null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch import logs with pagination and optional filters
   * @async
   * @param {GetImportLogsParams} [params]
   * @returns {Promise<void>}
   */
  const getImportLogs = async ({
    page = 1,
    per_page = ITEMS_PER_PAGE,
    filters = {},
  } = {}) => {
    try {
      setLoading(true);
      setError("");

      const query = new URLSearchParams();
      query.append("page", page);
      query.append("per_page", per_page);

      // Filters (e.g. filter[status]=completed, filter[type]=students)
      Object.keys(filters).forEach((field) => {
        if (filters[field] !== "" && filters[field] !== undefined) {
          query.append(`filter[${field}]`, filters[field]);
        }
      });

      const url = `${TENANT_DOMAIN}/import/logs?${query.toString()}`;

      const response = await axiosInstance.get(url);

      setLogs(response.data.data || []);
      setMeta(response.data.meta || null);
    } catch (err) {
      setLogs(null);
      setMeta(null);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    logs,
    meta,
    loading,
    error,
    getImportLogs,
  };
};

